import { useRef, useState } from 'react'
import {
  ActionIcon, Alert, Anchor, Badge, Button, FileButton, Group,
  Loader, Select, Stack, Table, Text,
} from '@mantine/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { IconDownload, IconPaperclip, IconTrash } from '@tabler/icons-react'
import {
  ADJUNTO_MAX_BYTES, CATEGORIAS_ADJUNTO, ETIQUETA_CATEGORIA, EXTENSIONES_PERMITIDAS,
  tamanoLegible, type Adjunto, type CategoriaAdjunto, type Estado, esEditableEstado,
} from '@serkat/shared'
import { ErrorApi, api } from '../../api/cliente'
import { fecha } from '../formato'

interface Props {
  documentoId: string
  estado: Estado
  puedeEditar: boolean
}

/**
 * Evidencia del documento: cotizaciones, facturas, guías.
 *
 * La subida no pasa por el cliente tipado porque envía `multipart/form-data`,
 * no JSON: `fetch` recibe el FormData tal cual y el navegador arma el borde.
 */
export function Adjuntos({ documentoId, estado, puedeEditar }: Props) {
  const qc = useQueryClient()
  const [categoria, setCategoria] = useState<CategoriaAdjunto>('COTIZACION')
  const reset = useRef<() => void>(null)

  const clave = ['adjuntos', documentoId]

  const { data, isPending } = useQuery({
    queryKey: clave,
    queryFn: () => api.get<Adjunto[]>(`/documentos/${documentoId}/adjuntos`),
  })

  const subir = useMutation({
    mutationFn: async (archivo: File) => {
      const cuerpo = new FormData()
      cuerpo.append('archivo', archivo)
      cuerpo.append('categoria', categoria)

      const res = await fetch(`/api/documentos/${documentoId}/adjuntos`, {
        method: 'POST', body: cuerpo, credentials: 'same-origin',
      })
      const json = await res.json().catch(() => null)
      if (!res.ok) {
        throw new ErrorApi(res.status, json?.error ?? 'desconocido', json?.mensaje ?? 'No se pudo subir')
      }
      return json as Adjunto
    },
    onSuccess: () => {
      notifications.show({ color: 'teal', message: 'Archivo adjuntado' })
      void qc.invalidateQueries({ queryKey: clave })
      reset.current?.()
    },
    onError: (e) =>
      notifications.show({
        color: 'red', title: 'No se pudo adjuntar',
        message: e instanceof ErrorApi ? e.message : 'Error inesperado',
      }),
  })

  const quitar = useMutation({
    mutationFn: (id: string) => api.delete(`/adjuntos/${id}`),
    onSuccess: () => {
      notifications.show({ color: 'teal', message: 'Adjunto retirado' })
      void qc.invalidateQueries({ queryKey: clave })
    },
    onError: (e) =>
      notifications.show({
        color: 'red', title: 'No se pudo retirar',
        message: e instanceof ErrorApi ? e.message : 'Error inesperado',
      }),
  })

  const abierto = esEditableEstado(estado)
  const puedeSubir = puedeEditar && abierto

  function elegir(archivo: File | null) {
    if (!archivo) return
    if (archivo.size > ADJUNTO_MAX_BYTES) {
      notifications.show({
        color: 'red', title: 'Archivo demasiado grande',
        message: `El límite es ${ADJUNTO_MAX_BYTES / 1024 / 1024} MB y este pesa ${tamanoLegible(archivo.size)}`,
      })
      return
    }
    subir.mutate(archivo)
  }

  if (isPending) return <Loader size="sm" />

  return (
    <Stack gap="sm">
      {puedeSubir ? (
        <Group align="flex-end" gap="sm">
          <Select
            label="Tipo de documento"
            data={CATEGORIAS_ADJUNTO.map((c) => ({ value: c, label: ETIQUETA_CATEGORIA[c] }))}
            value={categoria}
            onChange={(v) => setCategoria((v ?? 'OTRO') as CategoriaAdjunto)}
            w={230}
          />
          <FileButton resetRef={reset} onChange={elegir} accept={EXTENSIONES_PERMITIDAS}>
            {(props) => (
              <Button {...props} variant="light" loading={subir.isPending}
                leftSection={<IconPaperclip size={16} />}>
                Adjuntar archivo
              </Button>
            )}
          </FileButton>
          <Text size="xs" c="dimmed" pb={8}>
            {EXTENSIONES_PERMITIDAS} · hasta {ADJUNTO_MAX_BYTES / 1024 / 1024} MB
          </Text>
        </Group>
      ) : (
        <Alert color="gray" variant="light">
          {abierto
            ? 'Su rol no permite adjuntar archivos.'
            : `Un documento ${estado.toLowerCase()} queda cerrado: no admite cambios en sus adjuntos.`}
        </Alert>
      )}

      {(data ?? []).length === 0 ? (
        <Text size="sm" c="dimmed">Sin archivos adjuntos.</Text>
      ) : (
        <Table.ScrollContainer minWidth={560}>
          <Table withTableBorder verticalSpacing={6}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Archivo</Table.Th>
                <Table.Th w={170}>Tipo</Table.Th>
                <Table.Th w={90} ta="right">Tamaño</Table.Th>
                <Table.Th w={190}>Subido</Table.Th>
                <Table.Th w={80} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(data ?? []).map((a) => (
                <Table.Tr key={a.id}>
                  <Table.Td>
                    <Anchor href={`/api/adjuntos/${a.id}/descargar`} size="sm">
                      {a.nombreOriginal}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="light">{ETIQUETA_CATEGORIA[a.categoria]}</Badge>
                  </Table.Td>
                  <Table.Td ta="right" ff="monospace" fz="xs">{tamanoLegible(a.tamanoBytes)}</Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {fecha(a.creadoEn)}{a.subidoPor ? ` · ${a.subidoPor}` : ''}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} justify="flex-end">
                      <ActionIcon component="a" href={`/api/adjuntos/${a.id}/descargar`}
                        variant="subtle" title="Descargar">
                        <IconDownload size={15} />
                      </ActionIcon>
                      {puedeSubir && (
                        <ActionIcon variant="subtle" color="red" title="Quitar"
                          loading={quitar.isPending}
                          onClick={() => quitar.mutate(a.id)}>
                          <IconTrash size={15} />
                        </ActionIcon>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  )
}
